import { z } from "zod";

import { CaseType } from "../case-core";
import { commaSeparatedList } from "../utils";
import { EmbeddedBranchApiResponseSchema } from "./branch";
import { ClientStatus } from "./common";
import { CompanyClientSchema, CompanyType } from "./company";
import { PersonClientSchema } from "./person";

export const ClientSchema = z
  .discriminatedUnion("type", [PersonClientSchema, CompanyClientSchema])
  .describe("Persisted client document.");

export type Client = z.infer<typeof ClientSchema>;

/**
 * Lenient client shape for API responses where role-specific field sets may
 * omit required record fields.
 */
export const ClientApiResponseSchema = z
  .discriminatedUnion("type", [
    PersonClientSchema.partial().extend({
      type: PersonClientSchema.shape.type,
      id: PersonClientSchema.shape.id,
      name: PersonClientSchema.shape.name,
      availableCaseTypes: z.array(CaseType).default([]).meta({ visibleTo: "*" }),
    }),
    CompanyClientSchema.partial().extend({
      type: CompanyClientSchema.shape.type,
      id: CompanyClientSchema.shape.id,
      name: CompanyClientSchema.shape.name,
      availableCaseTypes: z.array(CaseType).default([]).meta({ visibleTo: "*" }),
      branches: z.array(EmbeddedBranchApiResponseSchema).optional().meta({ visibleTo: "*" }),
    }),
  ])
  .describe("Possibly-masked client document — see ClientSchema.");

export type ClientApiResponse = z.infer<typeof ClientApiResponseSchema>;

export const ListClientsQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(60).default(20),
    nextToken: z.string().optional(),
    q: z.string().optional(),
    status: commaSeparatedList(ClientStatus),
    companyType: commaSeparatedList(CompanyType),
    sortBy: z.enum(["createdAt", "name"]).default("name"),
    sortOrder: z.enum(["asc", "desc"]).default("asc"),
  })
  .describe("Paginated client list query parameters.");

export type ListClientsQuery = z.infer<typeof ListClientsQuerySchema>;

const ListClientRecordsResponseSchema = z
  .object({
    items: z.array(ClientSchema).meta({ visibleTo: "*" }),
    nextToken: z.string().optional().meta({ visibleTo: "*" }),
  })
  .describe("Paginated persisted client record list response.");

export type ListClientRecordsResponse = z.infer<typeof ListClientRecordsResponseSchema>;

export const ListClientApiResponsesSchema = z
  .object({
    items: z.array(ClientApiResponseSchema).meta({ visibleTo: "*" }),
    nextToken: z.string().optional().meta({ visibleTo: "*" }),
  })
  .describe("Paginated client list response — see ClientApiResponseSchema.");

export type ListClientApiResponses = z.infer<typeof ListClientApiResponsesSchema>;
